import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { message, Alert } from '@brokerbay/design-components';
import {
  getKeyholderBillingProfile,
  getJpmTransactionInfoKeyholder,
} from 'api/SupraController';
import type { IJpmcTransaction } from '@/app-package';
import { Modal, Spin } from 'components';
import { BillingInvoicesPageContext } from '../../pages/BillingInvoicesPage/BillingInvoicesPageContext';
import { ManageCardContext } from './ManageCardContext';

/**
 * Renders the Chase (JPMC) iframe in a modal.
 * – Shows a loader while the session‑id is being fetched by the context.

 *
 * 1. Loads hpfParent.min.js in the parent page *before* the iframe initialises.
 * 2. Exposes `handlePaymentErrors`, `completePayment`, and `cancelPayment` on
 *    `window` so the iframe can call them even though it is cross‑origin.
 * 3. Surfaces validation errors inside the modal
 * 4. Handles postMessage events from iframe (e.g., "what's this?" clicks)
 */

const hpfParentJS = `${process.env.JPMC_IFRAME_BASE_URL}js/hpfParent.min.js`;

declare global {
  interface Window {
    handlePaymentErrors?: (content: {
      messagId?: string;
      errorCode?: string[];
      subject?: string;
      reply?: boolean;
    }) => void;
    completePayment?: (txn: unknown) => void;
    cancelPayment?: (content: string) => void;
  }
}

const UpdateJPMCModal: React.FC = () => {
  const {
    modalVisible,
    setModalVisible,
    refetchCardInfo,
    onCardUpdateSuccess,
    jpmcSessionId,
    setLoadJPMCSession,
    orgId,
    billingCustomerNumber,
    jpmErrorMap,
  } = useContext(ManageCardContext);

  const {
    updateBillingProfile,
    updateProfileError,
    billingProfile,
    isUpdatingProfile,
  } = useContext(BillingInvoicesPageContext);

  const jpmcIframeUrl = `${process.env.JPMC_IFRAME_BASE_URL}1_1/?`;
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [helpContent, setHelpContent] = useState<string | null>(null);

  const closeAndResetIframe = useCallback(() => {
    setModalVisible(false);
    setIframeError(null);
    setHelpContent(null);
  }, [setModalVisible]);

  useEffect(() => {
    if (modalVisible) {
      // Load a new session for the next render
      setLoadJPMCSession(true);
    }
  }, [modalVisible, setLoadJPMCSession]);

  // Load hpfParent.min.js and register the global callbacks *once per open*.
  useEffect(() => {
    if (!modalVisible || !jpmcSessionId) return;

    // Inject the helper script
    const scriptId = 'hpf-parent-js';
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script');
      s.id = scriptId;
      s.src = hpfParentJS;
      s.async = false; // important
      document.head.appendChild(s);
    }

    // Handle postMessage events from the iframe
    const handlePostMessage = (event: MessageEvent) => {
      // Verify origin for security - ensure it's from the JPMC iframe
      const jpmcBaseUrl = process.env.JPMC_IFRAME_BASE_URL;
      if (jpmcBaseUrl) {
        const jpmcHostname = new URL(jpmcBaseUrl).hostname;

        if (!event.origin.includes(jpmcHostname)) {
          return;
        }
      }

      // Handle structured postMessage objects
      if (event.data && typeof event.data === 'object') {
        const { subject, content, reply, messageId } = event.data;

        if (subject === 'whatsThis') {
          const helpText =
            'CVC (Card Verification Code) is the three or four-digit security code on the back of your card.';

          if (content === 'cvv') {
            setHelpContent(helpText);
          }
        }
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Expose callback for HPF validation errors.
    window.handlePaymentErrors = ({ errorCode }) => {
      let friendly = 'Unable to update card.';

      if (errorCode && errorCode.length > 0) {
        const friendlyMessagesList = errorCode
          .map(code => jpmErrorMap[code]) // get the error strings
          .filter(Boolean); // get rid of nulls

        const uniqueFriendlyMessages = [...new Set(friendlyMessagesList)];

        if (uniqueFriendlyMessages.length > 0) {
          friendly = uniqueFriendlyMessages.join('\n');
        }
      }
      setIframeError(friendly);
    };

    // Expose callback for successful completion.
    window.completePayment = async () => {
      try {
        let profile = billingProfile;
        if (!billingProfile) {
          profile = await getKeyholderBillingProfile({
            orgId,
            billingCustomerNumber,
          });
        }

        if (!profile) {
          message.error('Failed to fetch billing profile');
          return;
        }

        const jpmcInfo: IJpmcTransaction = await getJpmTransactionInfoKeyholder(
          { JPMCUid: jpmcSessionId },
        );
        if (!jpmcInfo) {
          message.error('Failed to fetch JPMC transaction info');
          return;
        }

        await updateBillingProfile(
          {
            ...profile,
            JPUid: jpmcSessionId,
            CCNumber: jpmcInfo.CCNumber,
            MaskedCCNumber: jpmcInfo.MaskedCCNumber,
            CCExpiryDate: jpmcInfo.CCExpiryDate,
            CardholderName: jpmcInfo.CardholderName,
            CCType: jpmcInfo.CardType,
          },
          true,
        );

        if (!updateProfileError) {
          message.success('Card updated.');
        }
      } catch (e) {
        message.error('Failed to update card');
      } finally {
        setModalVisible(false);
        refetchCardInfo?.();
        onCardUpdateSuccess?.();
      }
    };

    window.cancelPayment = () => {
      closeAndResetIframe();
    };

    return () => {
      window.removeEventListener('message', handlePostMessage);
      delete window.handlePaymentErrors;
      delete window.completePayment;
      delete window.cancelPayment;
    };
  }, [
    modalVisible,
    jpmcSessionId,
    setModalVisible,
    refetchCardInfo,
    onCardUpdateSuccess,
    jpmErrorMap,
    orgId,
    billingCustomerNumber,
    closeAndResetIframe,
    billingProfile,
    updateBillingProfile,
    updateProfileError,
  ]);

  // Do not render anything if the modal isn't open
  if (!modalVisible) return null;

  return (
    <Modal
      title="Update Card"
      open={modalVisible}
      onCancel={() => closeAndResetIframe()}
      footer={null}
      width={420}
      destroyOnClose
    >
      <Spin
        spinning={!jpmcSessionId || isUpdatingProfile}
        center
        size="large"
      />

      {iframeError && !isUpdatingProfile && (
        <Alert
          type="error"
          message={iframeError}
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
        />
      )}

      {helpContent && !isUpdatingProfile && (
        <Alert
          type="info"
          message="Help Information"
          description={helpContent}
          closable
          onClose={() => setHelpContent(null)}
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
        />
      )}

      {jpmcSessionId && !isUpdatingProfile && (
        <IframeWrapper>
          <iframe
            key={jpmcSessionId ?? 0}
            id="jpmc-frame"
            src={`${jpmcIframeUrl}uID=${jpmcSessionId}`}
            width="100%"
            height="175"
            title="Chase payment iframe"
          />
        </IframeWrapper>
      )}
    </Modal>
  );
};

const IframeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default UpdateJPMCModal;
